import React, { useMemo, useState } from 'react';
import { Check, X, UserPlus } from 'lucide-react';
import { Button } from './ui/button';
import { useT } from '../contexts/LocaleProvider';

const empty = {
  full_name: '', email: '', poste: '', departement: '',
  telephone: '', localisation: '', manager_email: '', is_active: true,
};

export default function EmployeeComposer({ existing = [], initial, onSave, onCancel, mode = 'create' }) {
  const t = useT();
  const [draft, setDraft] = useState(() => initial ? { ...empty, ...initial, manager_email: initial.manager_email || '' } : empty);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const departments = useMemo(() => {
    const set = new Set();
    for (const e of existing) if (e.departement) set.add(e.departement);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [existing]);

  const managers = useMemo(
    () => [...existing].sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '')),
    [existing],
  );

  const submit = async () => {
    if (!draft.full_name.trim() || !draft.email.trim()) {
      setError(t('emp.required'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      // In edit mode the email is fixed (input is disabled). Strip it from
      // the PATCH payload so DRF doesn't run the unique validator against
      // an unchanged value · this can spuriously trigger when there's a
      // stale duplicate row in the DB.
      const payload = mode === 'edit' ? Object.fromEntries(
        Object.entries(draft).filter(([k]) => k !== 'email')
      ) : draft;
      await onSave(payload);
    } catch (e) {
      const msg = e.response?.data;
      if (msg && typeof msg === 'object') {
        setError(Object.entries(msg).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' · '));
      } else {
        setError(msg?.detail || t('editor.saveFailed'));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-[hsl(var(--surface))] border border-border rounded-lg p-5 mb-5">
      <div className="flex items-center gap-2 mb-4">
        <UserPlus className="h-4 w-4 text-[hsl(var(--text-soft))]" />
        <h2 className="text-[15px] font-semibold">
          {mode === 'edit' ? t('emp.editTitle') : t('emp.newTitle')}
        </h2>
      </div>

      {error && (
        <div className="mb-3 border border-[hsl(var(--danger)/.3)] bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))] px-3 py-2 text-[14px] rounded-md">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="field">
          <label className="field-label">{t('emp.fullName')} *</label>
          <input className="input" value={draft.full_name}
            onChange={e => setDraft({ ...draft, full_name: e.target.value })}
            placeholder={t('emp.fullName.ph')} />
        </div>
        <div className="field">
          <label className="field-label">{t('emp.email')} *</label>
          <input className="input font-num text-[14px]" type="email" value={draft.email}
            onChange={e => setDraft({ ...draft, email: e.target.value })}
            placeholder="camille@lamptwist.be" disabled={mode === 'edit'} />
        </div>
        <div className="field">
          <label className="field-label">{t('emp.role')}</label>
          <input className="input" value={draft.poste}
            onChange={e => setDraft({ ...draft, poste: e.target.value })}
            placeholder={t('emp.role.ph')} />
        </div>
        <div className="field">
          <label className="field-label">{t('emp.department')}</label>
          <input className="input" list="dept-list" value={draft.departement}
            onChange={e => setDraft({ ...draft, departement: e.target.value })}
            placeholder={t('emp.department.ph')} />
          <datalist id="dept-list">
            {departments.map(d => <option key={d} value={d} />)}
          </datalist>
        </div>
        <div className="field">
          <label className="field-label">{t('emp.phone')}</label>
          <input className="input font-num text-[14px]" value={draft.telephone}
            onChange={e => setDraft({ ...draft, telephone: e.target.value })}
            placeholder="+32 …" />
        </div>
        <div className="field">
          <label className="field-label">{t('emp.office')}</label>
          <input className="input" value={draft.localisation}
            onChange={e => setDraft({ ...draft, localisation: e.target.value })}
            placeholder={t('emp.office.ph')} />
        </div>
        <div className="field md:col-span-2">
          <label className="field-label">{t('emp.manager')}</label>
          <select value={draft.manager_email || ''}
            onChange={e => setDraft({ ...draft, manager_email: e.target.value })}>
            <option value="">{t('common.none')}</option>
            {managers.map(m => (
              <option key={m.id} value={m.email}>
                {m.full_name} ({m.email})
              </option>
            ))}
          </select>
        </div>
        <label className="flex items-center gap-2 text-[14px] md:col-span-2 mt-1">
          <input type="checkbox" checked={draft.is_active}
            onChange={e => setDraft({ ...draft, is_active: e.target.checked })} />
          {t('emp.active')}
        </label>
      </div>

      <div className="flex items-center justify-end gap-2 mt-4">
        <Button variant="outline" size="sm" onClick={onCancel} disabled={busy} className="gap-1.5">
          <X className="h-3.5 w-3.5" /> {t('common.cancel')}
        </Button>
        <Button size="sm" onClick={submit} disabled={busy} className="gap-1.5">
          <Check className="h-3.5 w-3.5" /> {mode === 'edit' ? t('common.save') : t('common.add')}
        </Button>
      </div>
    </section>
  );
}
